import React, { memo, useContext, useEffect, useState } from 'react';

import NoteBody from '@/components/NoteBody';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import NoteFilterForm from '@/forms/NoteFilterForm';
import { NoteFilters } from '@/forms/NoteFilterForm/types';
import useAPI from '@/hooks/useAPI';
import usePagination from '@/hooks/usePagination';
import useQueryString from '@/hooks/useQueryString';
import PersonContext from '@/pages/Person/PersonContext';
import NoteRepository from '@/repositories/NoteRepository';
import { Note, NoteParams } from '@/types/note';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import DualTime from '@/ui/components/DualTime';
import Heading from '@/ui/components/Heading/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import formatPersonName from '@/utils/formatPersonName';
import formatStaffName from '@/utils/formatStaffName';

const PersonNoteList = () => {
  const { person } = useContext(PersonContext);
  const [totalPages, setTotalPages] = useState<number>(0);

  const {
    params: queryParams,
    setParams: setQueryParams,
    hasQueryParams
  } = useQueryString<Partial<NoteParams>>();
  const [params, setParams] = useState<NoteParams>((): NoteParams => {
    // Initialize from query params
    if (hasQueryParams) {
      return {
        ...queryParams,
        page: queryParams.page || '1'
      } as NoteParams;
    }

    // Apply defaults
    return {
      note_type: undefined,
      page: '1'
    };
  });
  const noteRepository = NoteRepository();

  const setFilters = (filters: Partial<NoteFilters>) => {
    return setParams({ ...filters, page: '1' });
  };

  useEffect(() => {
    setQueryParams(params);
  }, [params, setQueryParams]);

  const {
    data: notesResponse,
    isPending: isLoadingNotes,
    isSuccess
  } = useAPI({
    queryKey: ['getNotes', person.id, params],
    queryFn: async () => {
      return await noteRepository.getNotes({
        params: { ...params, object_id: person.id, ordering: '-created_at' }
      });
    },
    enabled: params?.note_type !== 'member-availability'
  });

  useEffect(() => {
    if (isSuccess) {
      if (notesResponse?.page) {
        setParams({ ...params, page: notesResponse.page.toString() });
      }

      if (notesResponse?.total_pages) {
        setTotalPages(notesResponse.total_pages);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, notesResponse]);

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    parseInt(params.page, 10),
    totalPages
  );

  useEffect(() => {
    setParams((currentParams) => ({ ...currentParams, page: currentPage.toString() }));
  }, [currentPage]);

  const renderNote = (note: Note) => {
    if (!note?.body) return null;

    return (
      <Card>
        <NoteBody note={note} />

        {note?.author && (
          <p className="text-xs text-gray-500 w-3/5">
            Created <DualTime dateTime={new Date(note.created_at)} memberTZ={person.time_zone} />
            <span> by {formatStaffName({ staff: note.author })}</span>
          </p>
        )}
      </Card>
    );
  };

  return (
    <Container type="page">
      <PageHeader headingText="Notes" />
      <NoteFilterForm activeFilters={params} setFilters={setFilters} contentTypeName="person" />

      <Pagination
        currentPage={currentPage}
        goToPage={goToPage}
        nextPage={nextPage}
        prevPage={prevPage}
        totalPages={totalPages}>
        {isLoadingNotes && <LoadingSkeleton />}

        {!notesResponse?.results.length ? (
          <div className="text-center p-8">
            <Heading
              className="mp-sensitive"
              text={`No notes found for ${formatPersonName({ person })}`}
              value={3}
            />
          </div>
        ) : (
          <ul role="list">
            {notesResponse?.results.map((note) => (
              <li key={note.id}>{renderNote(note)}</li>
            ))}
          </ul>
        )}
      </Pagination>
    </Container>
  );
};

export default memo(PersonNoteList);
